/**
 * Treydora Library API (TreydoraEdBackend).
 * Env: REACT_APP_API_BASE_URL
 */
import { getApiBaseUrl } from "../utils/getApiBaseUrl";
import { authHeaders, getAuthToken } from "./authApi";

const API_BASE_URL = getApiBaseUrl();

/** TreydoraED catalog scope — must never appear on Treydora.com. */
const TREYDORAED_SITE_KEY = "treydoraED";

function isLegacyTreydoraLibraryAsset(asset) {
  const key = String(asset?.siteKey || "").trim();
  return key === "" || key !== TREYDORAED_SITE_KEY;
}

function filterLegacyLibraryAssets(items) {
  return (Array.isArray(items) ? items : []).filter(isLegacyTreydoraLibraryAsset);
}

const LIB_PREFIX = `${API_BASE_URL.replace(/\/$/, "")}/api/library`;

async function parseJson(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

function buildQuery(params) {
  const q = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** Unwrap { success, message, data } when present. */
function unwrap(body) {
  if (body && typeof body === "object" && body.success === true && body.data !== undefined) {
    return body.data;
  }
  return body;
}

/**
 * GET /api/library/assets
 * @param {Record<string, string|number|boolean|undefined>} [params] type, character, featured, status, limit, page
 */
export async function getLibraryAssets(params = {}) {
  try {
    const res = await fetch(`${LIB_PREFIX}/assets${buildQuery(params)}`);
    const body = await parseJson(res);
    if (!res.ok) {
      throw new Error("Unable to load library assets.");
    }
    const data = unwrap(body);
    if (data && Array.isArray(data.items)) {
      return { ...data, items: filterLegacyLibraryAssets(data.items) };
    }
    return data;
  } catch (e) {
    if (e && e.message === "Unable to load library assets.") throw e;
    throw new Error("Unable to load library assets.");
  }
}

/**
 * GET /api/library/assets/:slug
 * @param {string} slug
 */
export async function getLibraryAssetBySlug(slug) {
  try {
    const res = await fetch(`${LIB_PREFIX}/assets/${encodeURIComponent(slug)}`);
    const body = await parseJson(res);
    if (!res.ok) {
      throw new Error("Unable to load library item.");
    }
    const data = unwrap(body);
    if (data?.asset && !isLegacyTreydoraLibraryAsset(data.asset)) {
      throw new Error("Unable to load library item.");
    }
    return data;
  } catch (e) {
    if (e && e.message === "Unable to load library item.") throw e;
    throw new Error("Unable to load library item.");
  }
}

/**
 * GET /api/library/nft-comic-tiers — Featured NFT card row (Genesis; no auth).
 */
export async function getLibraryNftComicTiers() {
  const res = await fetch(`${LIB_PREFIX}/nft-comic-tiers`);
  const body = await parseJson(res);
  if (!res.ok) {
    throw new Error(body.message || body.error || "Unable to load NFT comic tiers.");
  }
  return unwrap(body);
}

/** GET /api/library/my-library (Bearer) */
export async function getMyLibrary() {
  const token = getAuthToken();
  if (!token) throw new Error("Not authenticated.");
  const res = await fetch(`${LIB_PREFIX}/my-library`, {
    headers: { ...authHeaders() },
  });
  const body = await parseJson(res);
  if (!res.ok) {
    throw new Error(body.message || body.error || `HTTP ${res.status}`);
  }
  return unwrap(body);
}

/** GET /api/library/viewer/:assetSlug (Bearer). Optional opts.entitlementId for NFT cover panel. */
export async function getViewerAsset(assetSlug, opts = {}) {
  const token = getAuthToken();
  if (!token) throw Object.assign(new Error("Not authenticated."), { status: 401 });
  const rawSlug = String(assetSlug || "").trim();
  const slugNorm = rawSlug.toLowerCase();
  const q = buildQuery({ entitlementId: opts.entitlementId });
  const res = await fetch(`${LIB_PREFIX}/viewer/${encodeURIComponent(slugNorm)}${q}`, {
    headers: { ...authHeaders() },
  });
  const body = await parseJson(res);
  if (!res.ok) {
    const err = new Error(body.message || body.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return unwrap(body);
}

/**
 * POST /api/library/viewer/:assetSlug/ask (Bearer) — entitled reader AI.
 * @param {string} assetSlug
 * @param {{ question: string, currentPage?: number|null, mode?: string, experienceHint?: "infer"|"story"|"lore"|"meaning" }} body
 */
export async function postLibraryReaderAsk(assetSlug, body) {
  const token = getAuthToken();
  if (!token) throw Object.assign(new Error("Not authenticated."), { status: 401 });
  const slugNorm = String(assetSlug || "").trim().toLowerCase();
  const question = String(body?.question ?? "").trim();
  const currentPage =
    body?.currentPage != null && body?.currentPage !== "" && Number.isFinite(Number(body.currentPage))
      ? Number(body.currentPage)
      : null;
  const mode = String(body?.mode || "spoiler_safe").trim().toLowerCase() || "spoiler_safe";
  const hintRaw = String(body?.experienceHint ?? "infer").trim().toLowerCase();
  const experienceHint = ["infer", "story", "lore", "meaning"].includes(hintRaw) ? hintRaw : "infer";
  const entitlementId = String(body?.entitlementId || "").trim();
  const res = await fetch(`${LIB_PREFIX}/viewer/${encodeURIComponent(slugNorm)}/ask`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify({
      question,
      ...(currentPage != null ? { currentPage } : {}),
      mode: mode === "lore_deep" ? "lore_deep" : "spoiler_safe",
      ...(experienceHint !== "infer" ? { experienceHint } : {}),
      ...(entitlementId ? { entitlementId } : {}),
    }),
  });
  const resJson = await parseJson(res);
  if (!res.ok) {
    const err = new Error(resJson.message || resJson.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return unwrap(resJson);
}

/** GET /api/library/nft/mint-challenge (Bearer) */
export async function getLibraryNftMintChallenge(entitlementId, walletAddress) {
  const token = getAuthToken();
  if (!token) throw Object.assign(new Error("Not authenticated."), { status: 401 });
  const eid = String(entitlementId || "").trim();
  const wa = String(walletAddress || "").trim();
  if (!eid || !wa) {
    throw Object.assign(new Error("entitlementId and walletAddress are required."), { status: 400 });
  }
  const res = await fetch(
    `${LIB_PREFIX}/nft/mint-challenge${buildQuery({ entitlementId: eid, walletAddress: wa })}`,
    { headers: { ...authHeaders() } }
  );
  const body = await parseJson(res);
  if (!res.ok) {
    const err = new Error(body.message || body.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = body.code;
    throw err;
  }
  return unwrap(body);
}

/** POST /api/library/nft/mint-entitlement (Bearer) */
export async function mintLibraryNftEntitlement({ entitlementId, walletAddress, signature, challengeId }) {
  const token = getAuthToken();
  if (!token) throw Object.assign(new Error("Not authenticated."), { status: 401 });
  const res = await fetch(`${LIB_PREFIX}/nft/mint-entitlement`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify({
      entitlementId: String(entitlementId || "").trim(),
      walletAddress: String(walletAddress || "").trim(),
      signature: String(signature || "").trim(),
      challengeId: String(challengeId || "").trim(),
    }),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    const err = new Error(body.message || body.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = body.code;
    throw err;
  }
  return unwrap(body);
}

/** POST /api/library/nft/wallet/create-intent (Bearer) */
export async function createLibraryNftWalletIntent({ editionId, quantity, walletAddress }) {
  const token = getAuthToken();
  if (!token) throw Object.assign(new Error("Not authenticated."), { status: 401 });
  const res = await fetch(`${LIB_PREFIX}/nft/wallet/create-intent`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({
      editionId: String(editionId || "").trim(),
      quantity: Math.max(1, Math.floor(Number(quantity) || 1)),
      walletAddress: String(walletAddress || "").trim(),
    }),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    const err = new Error(body.message || body.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = body.code;
    err.pendingIntent = body.pendingIntent;
    throw err;
  }
  return unwrap(body);
}

/** POST /api/library/nft/wallet/cancel-intent (Bearer) */
export async function cancelLibraryNftWalletIntent({ intentId }) {
  const token = getAuthToken();
  if (!token) throw Object.assign(new Error("Not authenticated."), { status: 401 });
  const res = await fetch(`${LIB_PREFIX}/nft/wallet/cancel-intent`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({
      intentId: String(intentId || "").trim(),
    }),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    const err = new Error(body.message || body.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = body.code;
    throw err;
  }
  return unwrap(body);
}

/** POST /api/library/nft/wallet/confirm (Bearer) */
export async function confirmLibraryNftWalletPurchase({ intentId, txHash }) {
  const token = getAuthToken();
  if (!token) throw Object.assign(new Error("Not authenticated."), { status: 401 });
  const res = await fetch(`${LIB_PREFIX}/nft/wallet/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({
      intentId: String(intentId || "").trim(),
      txHash: String(txHash || "").trim(),
    }),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    const err = new Error(body.message || body.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = body.code;
    throw err;
  }
  return unwrap(body);
}

/** GET /api/library/nft/wallet/intent/:intentId (Bearer) */
export async function getLibraryNftWalletIntent(intentId) {
  const token = getAuthToken();
  if (!token) throw Object.assign(new Error("Not authenticated."), { status: 401 });
  const id = String(intentId || "").trim();
  const res = await fetch(`${LIB_PREFIX}/nft/wallet/intent/${encodeURIComponent(id)}`, {
    headers: { ...authHeaders() },
  });
  const body = await parseJson(res);
  if (!res.ok) {
    const err = new Error(body.message || body.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return unwrap(body);
}

/**
 * POST /api/library/nft/comic-v2/sync-wallet-mint (Bearer)
 * After mintWithETH on TreydoraGenesisComic721V2, sync the receipt into My Library.
 * @param {{ txHash: string, chainId: number }} params
 */
export async function syncComicV2WalletMint({ txHash, chainId }) {
  const token = getAuthToken();
  if (!token) throw Object.assign(new Error("Not authenticated."), { status: 401 });
  const h = String(txHash || "").trim();
  const cid = Number(chainId);
  if (!/^0x[a-fA-F0-9]{64}$/.test(h)) {
    throw Object.assign(new Error("Invalid transaction hash."), { status: 400 });
  }
  if (!Number.isFinite(cid) || cid <= 0) {
    throw Object.assign(new Error("Invalid chainId."), { status: 400 });
  }
  const res = await fetch(`${LIB_PREFIX}/nft/comic-v2/sync-wallet-mint`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ txHash: h, chainId: cid }),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    const err = new Error(body.message || body.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = body.code;
    throw err;
  }
  return unwrap(body);
}
